import { axiosInstance } from "config/api";
import { cachedApiCall, getCacheKey, clearCacheEntry } from "utils/apiCache";

// In-flight request dedupe map: cacheKey -> Promise
const inFlightRequests = new Map();

/**
 * Doctor Service
 * Centralized service for all doctor-related API calls with caching
 */

/**
 * Fetch doctor profile with caching
 * @param {number} doctorId - Doctor ID
 * @param {Object} options - Cache options { force: boolean, ttl: number }
 * @returns {Promise<Object>} - Doctor profile data
 */
export const fetchDoctorProfile = async (doctorId, options = {}) => {
  const cacheKey = getCacheKey('doctor/profile', { doctor_id: doctorId });

  // Longer TTL for doctor profile (10 minutes) as it rarely changes
  const ttl = options.ttl || 10 * 60 * 1000;

  return cachedApiCall(
    cacheKey,
    async () => {
      console.log(`[DoctorService] Fetching profile for doctor ${doctorId}`);
      const response = await axiosInstance.get(`/api/doctor/profile?doctor_id=${doctorId}`);
      return response.data;
    },
    { ttl, force: options.force || false }
  );
};

/**
 * Fetch doctor recommendations with caching
 * @param {number} doctorId - Doctor ID
 * @param {Object} options - Cache options
 * @returns {Promise<Object>} - Doctor recommendations
 */
export const fetchDoctorRecommendations = async (doctorId, options = {}) => {
  const cacheKey = getCacheKey('doctor/recommendations', { doctor_id: doctorId });

  // 5-minute TTL for recommendations
  const ttl = options.ttl || 5 * 60 * 1000;

  return cachedApiCall(
    cacheKey,
    async () => {
      console.log(`[DoctorService] Fetching recommendations for doctor ${doctorId}`);
      const response = await axiosInstance.get(`api/prescription/getDoctorRecc?doctor_id=${doctorId}`);
      return response.data;
    },
    { ttl, force: options.force }
  );
};

/**
 * Fetch doctors by clinic
 * @param {number} clinicId - Clinic ID
 * @param {Object} options - Cache options
 * @returns {Promise<Array>} - List of doctors
 */
export const fetchDoctorsByClinic = async (clinicId, options = {}) => {
  const cacheKey = getCacheKey("doctor/byClinic", { clinic_id: clinicId });
  const ttl = options.ttl || 5 * 60 * 1000;

  // If a request for the same cacheKey is already in-flight and caller
  // didn't force a bypass, return the existing promise so only one
  // fetch executes at a time.
  if (!options.force && inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const list = await cachedApiCall(
        cacheKey,
        async () => {
          const response = await axiosInstance.get(
            `/api/doctor/getDoctorsByClinic?clinic_id=${clinicId}`,
          );
          // Normalize to an array of doctors
          if (response?.data) {
            localStorage.setItem("clinic_logo", response.data ?.doctors?.[0]?.clinic_icon || "");
          }
          return Array.isArray(response.data)
            ? response.data
            : response.data?.doctors || response.data?.data || [];
        },
        { ttl, force: options.force }
      );
      return list;
    } finally {
      // Ensure the in-flight entry is cleared once done (success or error)
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

/**
 * Fetch all doctors (no clinic filter) with dedupe
 */
export const fetchAllDoctors = async (options = {}) => {
  const cacheKey = getCacheKey("doctor/all", {});
  const ttl = options.ttl || 5 * 60 * 1000;

  if (!options.force && inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const list = await cachedApiCall(
        cacheKey,
        async () => {
          const response = await axiosInstance.get("/api/doctor/all");
          return Array.isArray(response.data)
            ? response.data
            : response.data?.doctors || response.data?.data || [];
        },
        { ttl, force: options.force }
      );
      return list;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

/**
 * Update doctor profile
 * Invalidates cache after update
 * @param {number} doctorId - Doctor ID
 * @param {Object} data - Updated doctor data
 * @returns {Promise<Object>} - Update response
 */
export const updateDoctorProfile = async (doctorId, data) => {
  const response = await axiosInstance.post('/api/doctor/update', data);

  // Invalidate cache for this doctor
  const cacheKey = getCacheKey('doctor/profile', { doctor_id: doctorId });
  clearCacheEntry(cacheKey);
  console.log(`[DoctorService] Cache invalidated for doctor ${doctorId}`);

  return response.data;
};

export default {
  fetchDoctorProfile,
  fetchDoctorRecommendations,
  fetchDoctorsByClinic,
  fetchAllDoctors,
  updateDoctorProfile,
};
